import { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  QrCodeIcon,
  Cog6ToothIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  SparklesIcon,
  TicketIcon,
  BanknotesIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  ChatBubbleBottomCenterTextIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  ChartBarIcon,
  UserPlusIcon,
  BellAlertIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronRightIcon,
  UserCircleIcon
} from "@heroicons/react/20/solid";
import { GlobalContext } from "./context/GlobalContext";
import Api from "./api/api";
import { Link, useLocation } from "react-router-dom";
import QRCodeComponent from "./components/QRComponent";
import ThemeToggle from "./components/ThemeToggle";
import { variantURL, fallbackToOriginal } from "./utils/imageVariant";
const defaultNavigation = [
  { name: "Dashboard", href: "/home", icon: HomeIcon, current: null },
  { name: "KPIs", href: "/kpis", icon: ChartBarIcon, current: null, isSuperAdmin: true },
  {
    name: "Users",
    href: "/users",
    icon: UsersIcon,
    current: null,
  },

  {
    name: "Events",
    href: "/events",
    icon: ChatBubbleLeftRightIcon,
    current: null,
  },
  { name: "Coupons", href: "/coupons", icon: TicketIcon, current: null, isSuperAdmin: true },
  { name: "Payouts", href: "/payouts", icon: BanknotesIcon, current: null, isSuperAdmin: true },
  { name: "Regions", href: "/regions", icon: GlobeAltIcon, current: null, isSuperAdmin: true },
  { name: "Host Applications", href: "/host-applications", icon: UserPlusIcon, current: null, isSuperAdmin: true },
  { name: "Host nudges", href: "/host-nudges", icon: BellAlertIcon, current: null, isSuperAdmin: true },
  {
    name: "Gist AI",
    icon: ChatBubbleOvalLeftEllipsisIcon,
    current: null,
    isSuperAdmin: true,
    children: [
      { name: "Client AI", href: "/gist-ai", icon: ChatBubbleOvalLeftEllipsisIcon, current: null },
      { name: "AI Launcher", href: "/gist-ai-launcher", icon: Squares2X2Icon, current: null },
      { name: "SMS Agent", href: "/conversations", icon: ChatBubbleLeftRightIcon, current: null },
      { name: "Admin Agent", href: "/admin-agent", icon: SparklesIcon, current: null },
    ],
  },
  {
    name: "Trust & Safety",
    icon: ShieldExclamationIcon,
    current: null,
    isSuperAdmin: true,
    children: [
      { name: "Issues", href: "/issues", icon: ExclamationTriangleIcon, current: null },
      { name: "Feedback", href: "/feedback", icon: ChatBubbleBottomCenterTextIcon, current: null },
      { name: "Arbitration", href: "/arbitration", icon: ShieldExclamationIcon, current: null },
    ],
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Renders a single sidebar entry. Leaf items are plain links; items with a
// `children` array (e.g. "Gist AI" encompassing its Conversations + AI Launcher
// sub-pages) render as a collapsible group that auto-expands on a child route.
function NavItem({ item }) {
  if (!item.children?.length) {
    return (
      <li>
        <a
          href={item.href}
          className={classNames(
            item.current
              ? "bg-primaryDark text-white"
              : "text-white hover:bg-primaryDark hover:text-white",
            "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6"
          )}
        >
          <item.icon className="h-6 w-6 shrink-0 text-white" aria-hidden="true" />
          {item.name}
        </a>
      </li>
    );
  }

  const childActive = item.children.some(
    (child) => child.href === window.location.pathname
  );

  return (
    <li>
      <Disclosure as="div" defaultOpen={childActive}>
        <DisclosureButton
          className={classNames(
            childActive
              ? "bg-primaryDark text-white"
              : "text-white hover:bg-primaryDark hover:text-white",
            "group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm font-semibold leading-6"
          )}
        >
          <item.icon className="h-6 w-6 shrink-0 text-white" aria-hidden="true" />
          {item.name}
          <ChevronRightIcon
            className="ml-auto h-5 w-5 shrink-0 text-white transition-transform group-data-[open]:rotate-90"
            aria-hidden="true"
          />
        </DisclosureButton>
        <DisclosurePanel as="ul" className="mt-1 space-y-1">
          {item.children.map((child) => (
            <li key={child.name}>
              <a
                href={child.href}
                className={classNames(
                  child.current
                    ? "bg-primaryDark text-white"
                    : "text-white hover:bg-primaryDark hover:text-white",
                  "block rounded-md py-2 pl-11 pr-2 text-sm font-semibold leading-6"
                )}
              >
                {child.name}
              </a>
            </li>
          ))}
        </DisclosurePanel>
      </Disclosure>
    </li>
  );
}

export default function Home({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navigation, setNavigation] = useState(defaultNavigation);
  const { globalState, setGlobalState } = useContext(GlobalContext);
  const [showQR, setShowQR] = useState(false);
  const location = useLocation();
  const showBioPrompt =
    globalState.vendor &&
    !globalState.vendor.bio?.trim() &&
    location.pathname !== "/settings";
  useEffect(() => {
    const currentPath = window.location.pathname;
    const filteredNavigation = defaultNavigation.filter(
      (item) => !item.isSuperAdmin || globalState.isSuperAdmin
    );

    setNavigation(() =>
      filteredNavigation.map((item) => ({
        ...item,
        current: item.href ? currentPath === item.href : false,
        children: item.children?.map((child) => ({
          ...child,
          current: currentPath === child.href,
        })),
      }))
    );
  }, [globalState.isSuperAdmin]);

  const onQRClick = () => {
    const showQRCode = !showQR
    setShowQR(showQRCode);
    setGlobalState((prevState) => ({
      ...prevState,
      isShowingQR: showQRCode
    }))

  };
  return (
    <>
      <div className="bg-slate-100 dark:bg-slate-900 min-h-screen">
        <Dialog
          className="relative z-50 lg:hidden"
          open={sidebarOpen}
          onClose={setSidebarOpen}
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-primary px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                  <img
                    className="h-16 w-auto"
                    src="/gist_logo.jpg"
                    alt="gist"
                  />
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation.map((item) => (
                          <NavItem key={item.name} item={item} />
                        ))}
                      </ul>
                    </li>

                    <li className="mt-auto">
                      <a
                        href="/settings"
                        className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:bg-primaryDark hover:text-white"
                      >
                        <Cog6ToothIcon
                          className="h-6 w-6 shrink-0 text-white group-hover:text-white"
                          aria-hidden="true"
                        />
                        Settings
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col transition-transform duration-300 ease-in-out ${globalState.sidebarCollapsed ? '-translate-x-full' : 'translate-x-0'}`}>
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-primary px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <img className="h-16 w-auto" src="/gist_logo.jpg" alt="gist" />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <NavItem key={item.name} item={item} />
                    ))}
                  </ul>
                </li>
                <li className="mt-auto">
                  <a
                    href="/settings"
                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:bg-primaryDark hover:text-white"
                  >
                    <Cog6ToothIcon
                      className="h-6 w-6 shrink-0 text-white group-hover:text-white"
                      aria-hidden="true"
                    />
                    Settings
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className={`transition-all duration-300 ease-in-out ${globalState.sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-72'}`}>
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              className={`-m-2.5 p-2.5 text-gray-700 dark:text-gray-200 ${globalState.sidebarCollapsed ? '' : 'lg:hidden'}`}
              onClick={() => {
                if (globalState.sidebarCollapsed) {
                  setGlobalState((prev) => ({ ...prev, sidebarCollapsed: false }));
                } else {
                  setSidebarOpen(true);
                }
              }}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div
              className={`h-6 w-px bg-gray-900/10 dark:bg-white/10 ${globalState.sidebarCollapsed ? '' : 'lg:hidden'}`}
              aria-hidden="true"
            />

            <div className="flex flex-1 gap-x-4 justify-end lg:gap-x-6">

              <div className="flex items-center gap-x-1 lg:gap-x-6">
                {/* Theme picker (compact, icon-only) */}
                <ThemeToggle showLabels={false} />

                {globalState.currentEventObj && (
                  <button
                    type="button"
                    className=" p-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-400 dark:hover:text-gray-300"
                    onClick={onQRClick}
                  >
                    <span className="sr-only">View QR Code</span>
                    <QrCodeIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                )}

                {/* Separator */}
                <div
                  className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10 dark:lg:bg-white/10"
                  aria-hidden="true"
                />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>

                    <span className="flex items-center mr-2">

                      {globalState.user?.photoURL ? (
                        <img
                          src={variantURL(globalState.user.photoURL, 200)}
                          onError={fallbackToOriginal(globalState.user.photoURL)}
                          alt=""
                          className="h-7 w-7 rounded-full object-cover mr-1"
                        />
                      ) : (
                        <UserCircleIcon
                          className="h-7 w-7 text-gray-600 dark:text-gray-300 mr-1"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-100 w-0 md:w-auto truncate"
                        aria-hidden="true"
                      >
                        {globalState.user?.name
                          || globalState.vendor?.name
                          || "Setup Account"}
                      </span>
                    </span>
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white dark:bg-gray-800 py-2 shadow-lg ring-1 ring-gray-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <MenuItem>
                      <Link
                        to="/settings"
                        className="block px-3 py-1 text-sm leading-6 text-gray-900 dark:text-gray-100"
                      >
                        Settings
                      </Link>
                    </MenuItem>
                    <MenuItem>
                      <button
                        onClick={Api.signOut}
                        className="block px-3 py-1 text-sm leading-6 text-gray-900 dark:text-gray-100"
                      >
                        Log out
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>

          <main className="py-2">
            {showBioPrompt && (
              <div className="px-4 sm:px-6 lg:px-8 pt-2">
                <div className="flex items-start gap-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                  <SparklesIcon className="h-5 w-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                  <div className="flex-1">
                    <p className="font-medium">Add a host bio</p>
                    <p className="text-amber-800 dark:text-amber-300">
                      Attendees see a blank profile on your event pages. Hosts with a bio fill seats faster.
                    </p>
                  </div>
                  <Link
                    to="/settings"
                    className="shrink-0 self-center rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                  >
                    Add bio
                  </Link>
                </div>
              </div>
            )}
            <div className="px-4 sm:px-6 lg:px-8">{children}</div>
            {showQR && globalState.currentEventObj && (
              <QRCodeComponent
                hideQR={onQRClick}
                currentEvent={globalState.currentEventObj}
              />
            )}
          </main>
        </div>
      </div>
    </>
  );
}

